/**
 * Memory verse of the week — card + games sheet.
 *
 * The card is the Grow tab's top section. It shows this-week's
 * passage, the household's progress (avatar row), and a Practice
 * button that opens a full-screen sheet with four games:
 *
 *   1. Word bank      — ~20% of words blank, tap from a shuffled bank
 *   2. First letters  — only first letter shown, type the rest
 *   3. From memory    — blank textarea, type what you remember
 *   4. Timed recall   — 30s to study the verse, then it hides and
 *                       you type it from memory
 *
 * "Memorized this week" = best_score >= 90. Once the current member
 * has memorized, the card collapses to a one-line confirmation pill
 * so it stops dominating the screen on return visits.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type MemoryVerseAttemptResponse,
  type MemoryVerseMode,
  type MemoryVerseThisWeek,
} from '../lib/api';
import { memoryVerseThisWeekQueryOptions } from '../lib/queries';

const MODE_LABEL: Record<MemoryVerseMode, string> = {
  word_bank: 'Word bank',
  first_letters: 'First letters',
  from_memory: 'From memory',
  timed_recall: 'Timed recall',
};

const COMPLETE_THRESHOLD = 90;

export function MemoryVerseCard() {
  const week = useQuery(memoryVerseThisWeekQueryOptions());
  const [sheetOpen, setSheetOpen] = useState(false);

  if (week.isLoading) {
    return (
      <section className="card p-5">
        <h2 className="font-serif text-2xl text-ink">Memory verse</h2>
        <p className="text-soft text-sm mt-2">Loading…</p>
      </section>
    );
  }
  if (!week.data) return null;

  const { verse, me, family } = week.data;
  if (!verse) {
    return (
      <section className="card p-5">
        <h2 className="font-serif text-2xl text-ink">Memory verse</h2>
        <p className="text-soft text-sm italic mt-2">
          No verse seeded for this week yet.
        </p>
      </section>
    );
  }

  const completed = me.completedAt != null;

  if (completed) {
    return (
      <section className="card p-4 flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-sage text-sm font-medium">
            ✓ Memorized this week — {verse.ref}
          </p>
          <p className="text-xs text-muted mt-0.5">
            Best: {me.bestMode ? MODE_LABEL[me.bestMode] : '—'} · {me.bestScore}%
            {me.streak > 1 ? ` · ${me.streak}-week streak` : ''}
          </p>
        </div>
        <button
          onClick={() => setSheetOpen(true)}
          className="text-xs text-oxblood font-medium shrink-0 ml-3"
        >
          Practice again
        </button>
        {sheetOpen && (
          <GamesSheet
            verse={verse}
            initialMode="from_memory"
            onClose={() => setSheetOpen(false)}
          />
        )}
      </section>
    );
  }

  return (
    <section className="card p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="kicker text-sage mb-1">Memory verse · this week</h2>
          <h3 className="font-serif text-xl text-ink">{verse.ref}</h3>
        </div>
        {me.streak > 0 && <span className="kicker text-soft">{me.streak}-wk streak</span>}
      </div>
      <p className="font-serif text-lg text-ink leading-relaxed mt-3">{verse.body}</p>

      <div className="mt-5 flex items-center justify-between">
        <FamilyProgressRow family={family} />
        <button
          onClick={() => setSheetOpen(true)}
          className="h-10 px-5 rounded-full bg-oxblood text-paper text-sm font-medium"
        >
          Practice
        </button>
      </div>

      {me.attempts > 0 && (
        <p className="text-xs text-muted mt-3">
          Best so far: {me.bestMode ? MODE_LABEL[me.bestMode] : '—'} · {me.bestScore}% (
          {me.attempts} {me.attempts === 1 ? 'try' : 'tries'})
        </p>
      )}

      {sheetOpen && (
        <GamesSheet
          verse={verse}
          initialMode="word_bank"
          onClose={() => setSheetOpen(false)}
        />
      )}
    </section>
  );
}

function FamilyProgressRow({ family }: { family: MemoryVerseThisWeek['family'] }) {
  return (
    <div className="flex items-center gap-1.5">
      {family.map((m) => {
        const done = m.completedAt != null;
        const initials = m.displayName
          .split(/\s+/)
          .map((s) => s[0]?.toUpperCase() ?? '')
          .slice(0, 2)
          .join('');
        return (
          <span
            key={m.memberId}
            title={`${m.displayName} · ${m.bestScore}%${done ? ' · done' : ''}`}
            className={`w-8 h-8 rounded-full text-xs font-medium flex items-center justify-center ${
              done ? 'bg-sage text-paper' : 'bg-line text-soft'
            }`}
          >
            {initials || '·'}
          </span>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------- games sheet

type Verse = { date: string; ref: string; body: string };

function GamesSheet({
  verse,
  initialMode,
  onClose,
}: {
  verse: Verse;
  initialMode: MemoryVerseMode;
  onClose: () => void;
}) {
  const [mode, setMode] = useState<MemoryVerseMode>(initialMode);
  const qc = useQueryClient();
  const submit = useMutation({
    mutationFn: (args: { mode: MemoryVerseMode; score: number }) =>
      api.post<MemoryVerseAttemptResponse>('/memory-verse/attempt', args),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['memory-verse', 'this-week'] });
    },
  });

  return (
    <div
      className="fixed inset-0 z-40 bg-ink/40 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-lg bg-paper rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
      >
        <header className="px-5 pt-5 pb-3 sticky top-0 bg-paper border-b border-line z-10">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-xl text-ink">{verse.ref}</h2>
            <button onClick={onClose} className="text-sm text-muted">
              Close
            </button>
          </div>
          <div className="mt-3 flex flex-wrap gap-1 rounded-2xl border border-line p-1 bg-card">
            {(
              [
                'word_bank',
                'first_letters',
                'from_memory',
                'timed_recall',
              ] as MemoryVerseMode[]
            ).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`px-3 h-8 rounded-full text-xs font-medium ${
                  mode === m ? 'bg-ink text-paper' : 'text-muted'
                }`}
              >
                {MODE_LABEL[m]}
              </button>
            ))}
          </div>
        </header>
        <div className="px-5 py-5">
          {mode === 'word_bank' && (
            <WordBankGame
              verse={verse}
              onScore={(score) => submit.mutate({ mode, score })}
              pending={submit.isPending}
              lastScore={submit.data?.me.bestScore}
            />
          )}
          {mode === 'first_letters' && (
            <FirstLettersGame
              verse={verse}
              onScore={(score) => submit.mutate({ mode, score })}
              pending={submit.isPending}
              lastScore={submit.data?.me.bestScore}
            />
          )}
          {mode === 'from_memory' && (
            <FromMemoryGame
              verse={verse}
              onScore={(score) => submit.mutate({ mode, score })}
              pending={submit.isPending}
              lastScore={submit.data?.me.bestScore}
            />
          )}
          {mode === 'timed_recall' && (
            <TimedRecallGame
              verse={verse}
              onScore={(score) => submit.mutate({ mode, score })}
              pending={submit.isPending}
              lastScore={submit.data?.me.bestScore}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------- text helpers

/** Splits body into a sequence of `{ word, suffix }` tokens preserving the
 *  spacing/punctuation between words for re-rendering. */
function tokenize(body: string): { word: string; suffix: string }[] {
  const out: { word: string; suffix: string }[] = [];
  const re = /(\S+)(\s*)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body))) {
    out.push({ word: m[1]!, suffix: m[2]! });
  }
  return out;
}

/** Strips punctuation + lowercases. Used for matching answers. */
function normalize(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9']/g, '');
}

function scoreWordMatch(guessed: string[], actual: string[]): number {
  if (actual.length === 0) return 100;
  let hit = 0;
  for (let i = 0; i < actual.length; i++) {
    if (normalize(guessed[i] ?? '') === normalize(actual[i] ?? '')) hit++;
  }
  return Math.round((hit / actual.length) * 100);
}

// -------------------------------------------------------------- Word bank

function WordBankGame({
  verse,
  onScore,
  pending,
  lastScore,
}: {
  verse: Verse;
  onScore: (score: number) => void;
  pending: boolean;
  lastScore?: number;
}) {
  const tokens = useMemo(() => tokenize(verse.body), [verse.body]);
  // Deterministic blank picker per verse: every 5th content word, plus the
  // longest word in each run of 5 — keeps it ~20% and consistent across
  // re-renders so the user can build muscle memory.
  const { blanks, bank } = useMemo(() => {
    const indices: number[] = [];
    for (let i = 0; i < tokens.length; i++) {
      if (i % 5 === 4 && tokens[i]!.word.length > 2) indices.push(i);
    }
    const wordsInBlanks = indices.map((i) => tokens[i]!.word);
    // Shuffle the bank deterministically (Fisher–Yates with a seed from
    // verse.date) so two members on the same week see the same order.
    const seed = verse.date.replace(/-/g, '');
    let s = Number(seed);
    const rand = () => {
      s = (s * 9301 + 49297) % 233280;
      return s / 233280;
    };
    const shuffled = wordsInBlanks.slice();
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!];
    }
    return { blanks: indices, bank: shuffled };
  }, [tokens, verse.date]);

  const [filled, setFilled] = useState<(string | null)[]>(() => blanks.map(() => null));
  const [submitted, setSubmitted] = useState(false);

  const handlePick = (word: string) => {
    setFilled((prev) => {
      const next = prev.slice();
      const slot = next.indexOf(null);
      if (slot === -1) return prev;
      next[slot] = word;
      return next;
    });
  };

  const handleClearSlot = (slotIndex: number) => {
    if (submitted) return;
    setFilled((prev) => {
      const next = prev.slice();
      next[slotIndex] = null;
      return next;
    });
  };

  const handleSubmit = () => {
    const guessed = blanks.map((_, i) => filled[i] ?? '');
    const actual = blanks.map((i) => tokens[i]!.word);
    const score = scoreWordMatch(guessed, actual);
    setSubmitted(true);
    onScore(score);
  };

  const usedFromBank = new Set(filled.filter((s): s is string => s != null));

  return (
    <div>
      <p className="text-sm text-soft mb-3">Tap the missing words from the bank below.</p>
      <p className="font-serif text-lg text-ink leading-relaxed">
        {tokens.map((tok, i) => {
          const blankIdx = blanks.indexOf(i);
          if (blankIdx === -1) {
            return (
              <span key={i}>
                {tok.word}
                {tok.suffix}
              </span>
            );
          }
          const val = filled[blankIdx];
          const correct =
            submitted && val != null ? normalize(val) === normalize(tok.word) : null;
          return (
            <span key={i}>
              <button
                onClick={() => handleClearSlot(blankIdx)}
                disabled={submitted}
                className={`inline-block min-w-[3rem] px-2 mx-0.5 border-b-2 align-baseline ${
                  correct === true
                    ? 'border-sage text-sage'
                    : correct === false
                      ? 'border-oxblood text-oxblood'
                      : val
                        ? 'border-ink text-ink'
                        : 'border-line text-muted'
                }`}
              >
                {val ?? ' '}
              </button>
              {tok.suffix}
            </span>
          );
        })}
      </p>

      {!submitted && (
        <>
          <div className="mt-5 flex flex-wrap gap-2">
            {bank.map((word, i) => (
              <button
                key={`${word}-${i}`}
                onClick={() => handlePick(word)}
                disabled={
                  usedFromBank.has(word) && bank.filter((b) => b === word).length === 1
                }
                className="px-3 h-9 rounded-full border border-line text-sm text-ink disabled:opacity-30 bg-card"
              >
                {word}
              </button>
            ))}
          </div>
          <button
            onClick={handleSubmit}
            disabled={filled.some((v) => v == null) || pending}
            className="mt-5 h-11 w-full rounded-full bg-oxblood text-paper text-sm font-medium disabled:opacity-50"
          >
            {pending ? 'Saving…' : 'Check'}
          </button>
        </>
      )}

      {submitted && lastScore != null && (
        <ScoreResult
          score={lastScore}
          onRetry={() => {
            setFilled(blanks.map(() => null));
            setSubmitted(false);
          }}
        />
      )}
    </div>
  );
}

// -------------------------------------------------------------- First letters

function FirstLettersGame({
  verse,
  onScore,
  pending,
  lastScore,
}: {
  verse: Verse;
  onScore: (score: number) => void;
  pending: boolean;
  lastScore?: number;
}) {
  const tokens = useMemo(() => tokenize(verse.body), [verse.body]);
  const [guesses, setGuesses] = useState<string[]>(() => tokens.map(() => ''));
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = () => {
    const actual = tokens.map((t) => t.word);
    const score = scoreWordMatch(guesses, actual);
    setSubmitted(true);
    onScore(score);
  };

  return (
    <div>
      <p className="text-sm text-soft mb-3">
        Only the first letter is shown. Type each word.
      </p>
      <div className="font-serif text-lg text-ink leading-relaxed flex flex-wrap gap-y-2">
        {tokens.map((tok, i) => {
          const first = tok.word[0] ?? '';
          const correct = submitted
            ? normalize(guesses[i] ?? '') === normalize(tok.word)
            : null;
          return (
            <span key={i} className="inline-flex items-baseline">
              <span className="text-muted">{first}</span>
              <input
                value={guesses[i] ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setGuesses((prev) => {
                    const next = prev.slice();
                    next[i] = v;
                    return next;
                  });
                }}
                disabled={submitted}
                aria-label={`Word ${i + 1}`}
                size={Math.max(2, tok.word.length - 1)}
                className={`bg-transparent border-b ${
                  correct === true
                    ? 'border-sage text-sage'
                    : correct === false
                      ? 'border-oxblood text-oxblood'
                      : 'border-line text-ink'
                } focus:outline-none focus:border-ink`}
              />
              <span>{tok.suffix}</span>
            </span>
          );
        })}
      </div>

      {!submitted && (
        <button
          onClick={handleSubmit}
          disabled={pending}
          className="mt-5 h-11 w-full rounded-full bg-oxblood text-paper text-sm font-medium disabled:opacity-50"
        >
          {pending ? 'Saving…' : 'Check'}
        </button>
      )}
      {submitted && lastScore != null && (
        <ScoreResult
          score={lastScore}
          onRetry={() => {
            setGuesses(tokens.map(() => ''));
            setSubmitted(false);
          }}
        />
      )}
    </div>
  );
}

// --------------------------------------------------------------- From memory

function FromMemoryGame({
  verse,
  onScore,
  pending,
  lastScore,
}: {
  verse: Verse;
  onScore: (score: number) => void;
  pending: boolean;
  lastScore?: number;
}) {
  const [text, setText] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const tokens = useMemo(() => tokenize(verse.body), [verse.body]);

  const handleSubmit = () => {
    const guessed = tokenize(text).map((t) => t.word);
    const actual = tokens.map((t) => t.word);
    const score = scoreWordMatch(guessed, actual);
    setSubmitted(true);
    onScore(score);
  };

  return (
    <div>
      <p className="text-sm text-soft mb-3">
        Type the verse from memory. We match word-by-word.
      </p>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={submitted}
        rows={6}
        placeholder={`${verse.ref}…`}
        className="card w-full p-3 font-serif text-lg text-ink resize-none focus:outline-none"
      />

      {submitted && (
        <p className="font-serif text-sm text-soft italic mt-3 border-l-2 border-line pl-3">
          {verse.body}
        </p>
      )}

      {!submitted && (
        <button
          onClick={handleSubmit}
          disabled={!text.trim() || pending}
          className="mt-5 h-11 w-full rounded-full bg-oxblood text-paper text-sm font-medium disabled:opacity-50"
        >
          {pending ? 'Saving…' : 'Check'}
        </button>
      )}
      {submitted && lastScore != null && (
        <ScoreResult
          score={lastScore}
          onRetry={() => {
            setText('');
            setSubmitted(false);
          }}
        />
      )}
    </div>
  );
}

// --------------------------------------------------------------- Timed recall

const STUDY_SECONDS = 30;

/**
 * Timed recall — flash-card style. Three phases:
 *
 *   idle    → explanation + "Start" button
 *   study   → verse visible, large countdown clock; auto-advances at 0
 *   recall  → verse hidden, textarea + Check; word-match scoring like
 *             FromMemoryGame.
 *
 * The pressure is on the *memorization* window, not the typing window —
 * once the clock hits zero the user has as long as they need to write
 * what they remember.
 */
function TimedRecallGame({
  verse,
  onScore,
  pending,
  lastScore,
}: {
  verse: Verse;
  onScore: (score: number) => void;
  pending: boolean;
  lastScore?: number;
}) {
  type Phase = 'idle' | 'study' | 'recall' | 'submitted';
  const [phase, setPhase] = useState<Phase>('idle');
  const [secondsLeft, setSecondsLeft] = useState(STUDY_SECONDS);
  const [text, setText] = useState('');
  const tokens = useMemo(() => tokenize(verse.body), [verse.body]);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (phase !== 'study') return;
    intervalRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          setPhase('recall');
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [phase]);

  const handleStart = () => {
    setSecondsLeft(STUDY_SECONDS);
    setText('');
    setPhase('study');
  };

  const handleSubmit = () => {
    const guessed = tokenize(text).map((t) => t.word);
    const actual = tokens.map((t) => t.word);
    const score = scoreWordMatch(guessed, actual);
    setPhase('submitted');
    onScore(score);
  };

  const handleRetry = () => {
    setPhase('idle');
    setSecondsLeft(STUDY_SECONDS);
    setText('');
  };

  if (phase === 'idle') {
    return (
      <div>
        <p className="text-sm text-soft mb-4">
          You'll have {STUDY_SECONDS} seconds to memorize the verse. When the clock hits
          zero it disappears and you type what you remember.
        </p>
        <button
          onClick={handleStart}
          className="h-11 w-full rounded-full bg-oxblood text-paper text-sm font-medium"
        >
          Start
        </button>
      </div>
    );
  }

  if (phase === 'study') {
    return (
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm text-soft">Memorize.</p>
          <p
            className={`font-serif text-2xl tabular-nums ${
              secondsLeft <= 5 ? 'text-oxblood' : 'text-ink'
            }`}
            aria-live="polite"
          >
            {secondsLeft}s
          </p>
        </div>
        <p className="font-serif text-lg text-ink leading-relaxed">{verse.body}</p>
      </div>
    );
  }

  if (phase === 'recall') {
    return (
      <div>
        <p className="text-sm text-soft mb-3">Time's up. Type what you remember.</p>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={6}
          autoFocus
          placeholder={`${verse.ref}…`}
          className="card w-full p-3 font-serif text-lg text-ink resize-none focus:outline-none"
        />
        <button
          onClick={handleSubmit}
          disabled={!text.trim() || pending}
          className="mt-5 h-11 w-full rounded-full bg-oxblood text-paper text-sm font-medium disabled:opacity-50"
        >
          {pending ? 'Saving…' : 'Check'}
        </button>
      </div>
    );
  }

  // submitted
  return (
    <div>
      <p className="font-serif text-sm text-soft italic border-l-2 border-line pl-3">
        {verse.body}
      </p>
      {lastScore != null && <ScoreResult score={lastScore} onRetry={handleRetry} />}
    </div>
  );
}

function ScoreResult({ score, onRetry }: { score: number; onRetry: () => void }) {
  const memorized = score >= COMPLETE_THRESHOLD;
  return (
    <div className="mt-5 text-center">
      <p className={`font-serif text-2xl ${memorized ? 'text-sage' : 'text-ink'}`}>
        {score}%
      </p>
      <p className="text-sm text-muted mt-1">
        {memorized ? '✓ Memorized — nice work.' : 'Keep going.'}
      </p>
      <button
        onClick={onRetry}
        className="mt-4 h-10 px-5 rounded-full border border-line text-sm text-ink"
      >
        Try again
      </button>
    </div>
  );
}
